package net.frozenbit.games.soundgame

import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeContentPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.unit.dp
import androidx.lifecycle.viewmodel.compose.viewModel
import net.frozenbit.games.soundgame.audio.AudioManager
import net.frozenbit.games.soundgame.audio.MusicManager
import net.frozenbit.games.soundgame.components.rememberPackedBitmapPainter
import net.frozenbit.games.soundgame.theme.lightScheme
import net.frozenbit.games.soundgame.ui.ControllerScreen
import net.frozenbit.games.soundgame.ui.HostScreen
import net.frozenbit.games.soundgame.viewmodels.ControllerViewModel
import net.frozenbit.games.soundgame.viewmodels.HostViewModel
import org.jetbrains.compose.resources.imageResource
import org.jetbrains.compose.resources.painterResource
import soundgame.app.shared.generated.resources.Res
import soundgame.app.shared.generated.resources.bg_record
import soundgame.app.shared.generated.resources.bg_salad
import soundgame.app.shared.generated.resources.logo_header
import kotlin.time.Duration.Companion.seconds

@Composable
fun HostApp(
    keyValueStore: KeyValueStore,
    sessionKeyValueStore: KeyValueStore,
    audioManager: AudioManager,
    musicManager: MusicManager,
    viewModel: HostViewModel = viewModel { HostViewModel(keyValueStore, sessionKeyValueStore, audioManager, musicManager) }
) {
    MaterialTheme(
        colorScheme = lightScheme,
    ) {
        val uiState by viewModel.uiState.collectAsState()
        val recordRotation by rememberInfiniteTransition(label = "record rotation").animateFloat(
            initialValue = 0f,
            targetValue = 360f,
            animationSpec = infiniteRepeatable(
                animation = tween(durationMillis = 20.seconds.inWholeMilliseconds.toInt(), easing = LinearEasing),
                repeatMode = RepeatMode.Restart,
            ),
            label = "record rotation angle",
        )
        Box(
            contentAlignment = Alignment.Center,
            modifier = Modifier
                .background(MaterialTheme.colorScheme.primaryContainer)
                .safeContentPadding()
                .fillMaxSize()
        ) {
            Image(
                painter = rememberPackedBitmapPainter(
                    bitmaps = setOf(imageResource(Res.drawable.bg_salad)),
                    tileSize = 300.dp,
                    maxRotationDegrees = 180f,
                ),
                contentDescription = null,
                modifier = Modifier
                    .fillMaxSize()
                    .alpha(0.2f)
            )
            Image(
                painter = painterResource(Res.drawable.bg_record),
                contentDescription = null,
                modifier = Modifier
                    .align(Alignment.CenterEnd)
                    .size(800.dp)
                    .offset(x = 390.dp, y = 200.dp)
                    .rotate(recordRotation)
            )
            Image(
                painter = painterResource(Res.drawable.logo_header),
                contentDescription = null,
                modifier = Modifier
                    .background(
                        MaterialTheme.colorScheme.primaryContainer,
                        RoundedCornerShape(100),
                    )
                    .align(Alignment.TopCenter)
                    .padding(64.dp, 16.dp, 64.dp, 24.dp)
            )
            HostScreen(
                hostUiState = uiState,
                countdownTickState = viewModel.countdownTicks.collectAsState(null),
                onStartGameClick = { viewModel.startHosting() },
            )
        }
    }
}

@Composable
fun ControllerApp(
    keyValueStore: KeyValueStore,
    viewModel: ControllerViewModel = viewModel { ControllerViewModel(keyValueStore) }
) {
    MaterialTheme(
        colorScheme = lightScheme,
    ) {
        val uiState by viewModel.uiState
        Column(
            modifier = Modifier
                .background(MaterialTheme.colorScheme.primaryContainer)
                .safeContentPadding()
                .fillMaxSize(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            ControllerScreen(
                controllerUiState = uiState,
                joinCodeState = viewModel.joinCodeInput,
                joinCodeTransformation = viewModel.transformJoinCodeInput,
                playerNameState = viewModel.playerNameInput,
                selectedAvatarIndex = viewModel.selectedAvatarIndex.value,
                countdownTextState = viewModel.countdownTicks.collectAsState(null),
                onJoinGameClick = { viewModel.start() },
                onStartButtonClick = { viewModel.startGame() },
                onAvatarSelected = { viewModel.selectAvatar(it) },
                onPromptSelected = { viewModel.selectPrompt(it) },
                onSoundSelected = { viewModel.selectSound(it) },
                onVoteSelected = { viewModel.selectVote(it) },
                onReplayClicked = { viewModel.replaySound(it) },
                onGameOverActionSelected = { viewModel.selectGameOverAction(it) }
            )
        }
    }
}